// Warstwa platformy: ta sama gra na web, iOS i Androidzie. Wszystko, co zależy od urządzenia, jest tutaj.
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';

export const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';
export const isNative = Capacitor.isNativePlatform();
export const isTouch = isNative || 'ontouchstart' in window || navigator.maxTouchPoints > 0;

/** Bezpieczne przechowywanie tokenów: natywnie Preferences (UserDefaults / SharedPreferences), w web localStorage. */
export const storage = {
  async get(key: string): Promise<string | null> {
    if (isNative) return (await Preferences.get({ key })).value;
    try { return localStorage.getItem(key); } catch { return null; }
  },
  async set(key: string, value: string): Promise<void> {
    if (isNative) return Preferences.set({ key, value });
    try { localStorage.setItem(key, value); } catch { /* tryb prywatny */ }
  },
  async remove(key: string): Promise<void> {
    if (isNative) return Preferences.remove({ key });
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

export async function haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
  if (!isNative) { navigator.vibrate?.(kind === 'heavy' ? 60 : 15); return; }
  const style = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy }[kind];
  try { await Haptics.impact({ style }); } catch { /* brak silnika wibracji */ }
}

export async function initPlatform(): Promise<void> {
  document.body.classList.add(`platform-${platform}`, isTouch ? 'touch' : 'mouse');
  if (isNative) {
    try { await StatusBar.hide(); } catch { /* Android starszy */ }
    try { await ScreenOrientation.lock({ orientation: 'landscape' }); } catch { /* iPad / web */ }
    await SplashScreen.hide();
  }
}

/** Gra w tle: zamykamy połączenie (oszczędność baterii, iOS i tak zawiesza WebView), po powrocie – wznawiamy. */
export function onAppState(handler: (active: boolean) => void): void {
  if (isNative) App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => handler(isActive));
  else document.addEventListener('visibilitychange', () => handler(!document.hidden));
}

export function onNetwork(handler: (online: boolean) => void): void {
  if (isNative) Network.addListener('networkStatusChange', (s: { connected: boolean }) => handler(s.connected));
  else { window.addEventListener('online', () => handler(true)); window.addEventListener('offline', () => handler(false)); }
}

/** Android: przycisk wstecz zamyka panele zamiast wychodzić z gry. */
export function onBackButton(handler: () => boolean): void {
  if (platform === 'android') App.addListener('backButton', () => { if (!handler()) App.minimizeApp(); });
}

/** Powiadomienia push (alarmy epidemiologiczne, gildia). Zwraca token urządzenia albo null. */
export async function registerPush(): Promise<string | null> {
  if (!isNative) return null;
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return null;
  return new Promise((resolve) => {
    PushNotifications.addListener('registration', (t: { value: string }) => resolve(t.value));
    PushNotifications.addListener('registrationError', () => resolve(null));
    PushNotifications.register();
  });
}
